import {
  ComponentType,
  memo,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';

// import Sentry SDK directly since this is published package
import * as Sentry from '@sentry/browser';
import { injectIntl, InjectedIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Spinner from '@atlaskit/spinner';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import urls from '../urls';

import messages from './account-menu.i18n';
import { DropdownItem, DropdownItemGroup } from './dropdown-helpers';

type Props = {
  /* eslint-disable react/no-unused-prop-types */
  userWorkspaceSlug: string | undefined;
  intl: InjectedIntl;
  recentlyViewedWorkspacesUuids: string[] | undefined;
  workspaceLinkComponent?: ComponentType<PropsWithChildren<any>>;
  hasSeparator: boolean;
  setHorizontalProfilePopupClose?: () => void;
};

type WorkspacesResponse = {
  values: {
    uuid: string;
    name: string;
    links: {
      html: { href: string };
      html_overview: { href: string };
      avatar: { href: string };
    };
  }[];
};

type WorkspaceInfo = {
  uuid: string;
  name: string;
  overview: string;
  avatar: string;
};

const WORKSPACES_DISPLAY_COUNT = 5;

type WorkspacesKeyedById = {
  [key: string]: WorkspaceInfo;
};
export const AccountMenuWorkspaces = injectIntl(
  memo(
    ({
      intl,
      recentlyViewedWorkspacesUuids = [],
      workspaceLinkComponent,
      hasSeparator,
      setHorizontalProfilePopupClose,
    }: Props) => {
      const isAccountWorkspacesEnabled = FeatureGates.checkGate(
        StatsigFeatureKeys.frontbucketAccountWorkspaces
      );
      const workspaceHeaderLabel = intl.formatMessage(
        messages.workspacesHeader
      );

      const [workspaces, setWorkspaces] = useState<WorkspacesKeyedById>({});
      const [hasLoaded, setHasLoaded] = useState<boolean>(false);

      useEffect(() => {
        if (!recentlyViewedWorkspacesUuids.length) {
          return;
        }

        try {
          // Build the query to be sent to the API.
          const queries: string[] =
            recentlyViewedWorkspacesUuids
              ?.slice(0, WORKSPACES_DISPLAY_COUNT)
              .map(uuid => `uuid="${uuid}"`) || [];
          fetch(
            `/!api/2.0/workspaces?fields=values.uuid,values.name,values.links.html.href,values.links.html_overview.href,values.links.avatar.href&pagelen=${WORKSPACES_DISPLAY_COUNT}&q=${encodeURIComponent(
              queries.join(' OR ')
            )}`
          )
            .then(resp => resp.json())
            .then((body: WorkspacesResponse) => {
              const workspaceData = body.values.reduce(
                (obj: WorkspacesKeyedById, ws) => {
                  obj[ws.uuid] = {
                    uuid: ws.uuid,
                    name: ws.name,
                    overview: ws.links.html_overview.href,
                    avatar: ws.links.avatar.href,
                  };
                  return obj;
                },
                {}
              );
              setWorkspaces(workspaceData);
            });
        } catch (e) {
          Sentry.captureException(e);
          setWorkspaces({});
        } finally {
          setHasLoaded(true);
        }
        // These two dependencies are generally static so this is effectively componentDidMount
      }, [recentlyViewedWorkspacesUuids]);

      return (
        <DropdownItemGroup
          title={workspaceHeaderLabel}
          key="workspaces-group"
          hasSeparator={hasSeparator}
        >
          {recentlyViewedWorkspacesUuids?.length > 0 &&
            (hasLoaded ? (
              recentlyViewedWorkspacesUuids.map(uuid => {
                const ws = workspaces[uuid];
                return ws ? (
                  <DropdownItem
                    href={ws.overview}
                    key={ws.uuid}
                    linkComponent={workspaceLinkComponent || undefined}
                    iconBefore={
                      <Avatar
                        size="xsmall"
                        appearance="square"
                        src={ws.avatar}
                      />
                    }
                    shouldTitleWrap={false}
                  >
                    {ws.name}
                  </DropdownItem>
                ) : null;
              })
            ) : (
              <DropdownItem>
                <Spinner size="small" />
              </DropdownItem>
            ))}
          <DropdownItem
            href={urls.ui.allWorkspaces()}
            key={urls.ui.allWorkspaces()}
            linkComponent={
              isAccountWorkspacesEnabled && workspaceLinkComponent
                ? workspaceLinkComponent
                : undefined
            }
            onClick={setHorizontalProfilePopupClose}
          >
            {intl.formatMessage(messages.viewAllWorkspacesLink)}
          </DropdownItem>
        </DropdownItemGroup>
      );
    }
  )
);
